<template>
  <div :class="['heatmap-calendar-widget', { 'with-card': bordered }]">
    <a-card v-if="bordered" :bordered="bordered">
      <div v-if="title" slot="title">
        <h3 class="heatmap-title">{{ title }}</h3>
      </div>
      <div class="heatmap-content">
        <div class="heatmap-header">
          <div class="heatmap-legend">
            <span class="legend-label">{{ legendLessText }}</span>
            <div
              v-for="(color, index) in colorLevels"
              :key="index"
              class="legend-item"
              :style="{ backgroundColor: color }"
              :title="`级别 ${index}`"
            ></div>
            <span class="legend-label">{{ legendMoreText }}</span>
          </div>
        </div>
        
        <div class="heatmap-container">
          <div class="heatmap-months">
            <div
              v-for="month in months"
              :key="month.name"
              class="month-label"
              :style="{ left: month.offset + 'px' }"
            >
              {{ month.name }}
            </div>
          </div>
          
          <div class="heatmap-body">
            <div class="heatmap-weekdays">
              <div class="weekday-label">{{ weekdayLabels[0] }}</div>
              <div class="weekday-label">{{ weekdayLabels[2] }}</div>
              <div class="weekday-label">{{ weekdayLabels[4] }}</div>
            </div>
            
            <div class="heatmap-grid">
              <div
                v-for="(week, weekIndex) in weeks"
                :key="weekIndex"
                class="week-column"
              >
                <div
                  v-for="(day, dayIndex) in week"
                  :key="dayIndex"
                  class="day-cell"
                  :class="{ 'empty-cell': !day.date }"
                  :style="{ backgroundColor: day.color }"
                  :title="day.tooltip"
                  @click="handleCellClick(day)"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-card>
    
    <div v-else class="heatmap-content">
      <div class="heatmap-header">
        <div class="heatmap-legend">
          <span class="legend-label">{{ legendLessText }}</span>
          <div
            v-for="(color, index) in colorLevels"
            :key="index"
            class="legend-item"
            :style="{ backgroundColor: color }"
            :title="`级别 ${index}`"
          ></div>
          <span class="legend-label">{{ legendMoreText }}</span>
        </div>
      </div>
      
      <div class="heatmap-container">
        <div class="heatmap-months">
          <div
            v-for="month in months"
            :key="month.name"
            class="month-label"
            :style="{ left: month.offset + 'px' }"
          >
            {{ month.name }}
          </div>
        </div>
        
        <div class="heatmap-body">
          <div class="heatmap-weekdays">
            <div class="weekday-label">{{ weekdayLabels[0] }}</div>
            <div class="weekday-label">{{ weekdayLabels[2] }}</div>
            <div class="weekday-label">{{ weekdayLabels[4] }}</div>
          </div>
          
          <div class="heatmap-grid">
            <div
              v-for="(week, weekIndex) in weeks"
              :key="weekIndex"
              class="week-column"
            >
              <div
                v-for="(day, dayIndex) in week"
                :key="dayIndex"
                class="day-cell"
                :class="{ 'empty-cell': !day.date }"
                :style="{ backgroundColor: day.color }"
                :title="day.tooltip"
                @click="handleCellClick(day)"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HeatmapCalendar',
  props: {
    // 组件标题
    title: {
      type: String,
      default: ''
    },
    // 是否显示边框
    bordered: {
      type: Boolean,
      default: true
    },
    // 数据数组，格式: [{ date: '2024-01-01', value: 10, label: '自定义标签' }, ...]
    data: {
      type: Array,
      default: () => []
    },
    // 年份，默认当前年份
    year: {
      type: Number,
      default: () => new Date().getFullYear()
    },
    // 颜色级别数组，从低到高
    colorLevels: {
      type: Array,
      default: () => ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39']
    },
    // 级别阈值，用于将value映射到颜色级别
    // 如果不提供，将自动根据数据计算
    levelThresholds: {
      type: Array,
      default: null
    },
    // 星期标签
    weekdayLabels: {
      type: Array,
      default: () => ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
    },
    // 月份标签
    monthLabels: {
      type: Array,
      default: () => ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
    },
    // 图例文本
    legendLessText: {
      type: String,
      default: '少'
    },
    legendMoreText: {
      type: String,
      default: '多'
    },
    // 自定义 tooltip 格式化函数
    tooltipFormatter: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      weeks: [],
      months: [],
      dataMap: {},
      thresholds: []
    }
  },
  watch: {
    data: {
      handler() {
        this.initCalendar()
      },
      deep: true
    },
    year() {
      this.initCalendar()
    }
  },
  mounted() {
    this.initCalendar()
  },
  methods: {
    initCalendar() {
      this.buildDataMap()
      this.calculateThresholds()
      this.buildWeeks()
      this.buildMonths()
    },
    
    // 构建数据映射表
    buildDataMap() {
      this.dataMap = {}
      this.data.forEach(item => {
        this.dataMap[item.date] = item
      })
    },
    
    // 计算阈值
    calculateThresholds() {
      if (this.levelThresholds) {
        this.thresholds = this.levelThresholds
        return
      }
      
      // 自动计算阈值
      const values = this.data.map(item => item.value).filter(v => v > 0).sort((a, b) => a - b)
      
      if (values.length === 0) {
        this.thresholds = [0, 0, 0, 0]
        return
      }
      
      const max = values[values.length - 1]
      const levelCount = this.colorLevels.length - 1
      
      this.thresholds = []
      for (let i = 1; i <= levelCount; i++) {
        this.thresholds.push(Math.ceil((max / levelCount) * i))
      }
    },
    
    // 根据值获取颜色级别
    getColorLevel(value) {
      if (value === 0 || value === null || value === undefined) {
        return 0
      }
      
      for (let i = 0; i < this.thresholds.length; i++) {
        if (value <= this.thresholds[i]) {
          return i + 1
        }
      }
      
      return this.colorLevels.length - 1
    },
    
    // 构建周数据
    buildWeeks() {
      const startDate = new Date(this.year, 0, 1)
      const endDate = new Date(this.year, 11, 31)
      
      // 找到第一周的周一
      const firstDayOfWeek = startDate.getDay()
      const startDayIndex = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
      
      this.weeks = []
      let currentWeek = []
      
      // 填充第一周的空白
      for (let i = 0; i < startDayIndex; i++) {
        currentWeek.push({ date: null, value: 0, color: 'transparent' })
      }
      
      // 遍历一年中的每一天
      const currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        const dateStr = this.formatDate(currentDate)
        const dayData = this.dataMap[dateStr] || {}
        const value = dayData.value || 0
        const colorLevel = this.getColorLevel(value)
        
        currentWeek.push({
          date: dateStr,
          value: value,
          color: this.colorLevels[colorLevel],
          label: dayData.label,
          tooltip: this.getTooltip(dateStr, value, dayData.label)
        })
        
        // 如果是周日，开始新的一周
        if (currentDate.getDay() === 0) {
          this.weeks.push(currentWeek)
          currentWeek = []
        }
        
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      // 填充最后一周的空白
      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push({ date: null, value: 0, color: 'transparent' })
        }
        this.weeks.push(currentWeek)
      }
    },
    
    // 构建月份标签
    buildMonths() {
      this.months = []
      const cellSize = 12
      const cellGap = 3
      const columnWidth = cellSize + cellGap
      
      let weekIndex = 0
      for (let month = 0; month < 12; month++) {
        const firstDayOfMonth = new Date(this.year, month, 1)
        
        // 计算该月第一天在第几周
        const startDate = new Date(this.year, 0, 1)
        const daysDiff = Math.floor((firstDayOfMonth - startDate) / (1000 * 60 * 60 * 24))
        const firstDayOfWeek = startDate.getDay()
        const startDayIndex = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
        weekIndex = Math.floor((daysDiff + startDayIndex) / 7)
        
        this.months.push({
          name: this.monthLabels[month],
          offset: weekIndex * columnWidth
        })
      }
    },
    
    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 获取 tooltip
    getTooltip(date, value, label) {
      if (this.tooltipFormatter) {
        return this.tooltipFormatter(date, value, label)
      }
      
      let tooltip = `${date}`
      if (label) {
        tooltip += `: ${label}`
      } else if (value > 0) {
        tooltip += `: ${value}`
      } else {
        tooltip += ': 无数据'
      }
      return tooltip
    },
    
    // 处理单元格点击事件
    handleCellClick(day) {
      if (day.date) {
        this.$emit('cell-click', {
          date: day.date,
          value: day.value,
          label: day.label
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.heatmap-calendar-widget {
  .heatmap-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    .heatmap-title {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
    }
    
    .heatmap-legend {
      display: flex;
      align-items: center;
      gap: 4px;
      
      .legend-label {
        font-size: 12px;
        color: #666;
      }
      
      .legend-item {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid rgba(27, 31, 35, 0.06);
      }
    }
  }
  
  .heatmap-container {
    position: relative;
    
    .heatmap-months {
      position: relative;
      height: 20px;
      margin-bottom: 4px;
      margin-left: 40px;
      
      .month-label {
        position: absolute;
        font-size: 12px;
        color: #666;
      }
    }
    
    .heatmap-body {
      display: flex;
      
      .heatmap-weekdays {
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        width: 40px;
        padding-right: 8px;
        
        .weekday-label {
          height: 12px;
          font-size: 10px;
          color: #666;
          line-height: 12px;
          text-align: right;
        }
      }
      
      .heatmap-grid {
        display: flex;
        gap: 3px;
        overflow-x: auto;
        
        .week-column {
          display: flex;
          flex-direction: column;
          gap: 3px;
          
          .day-cell {
            width: 12px;
            height: 12px;
            border-radius: 2px;
            cursor: pointer;
            transition: all 0.2s;
            border: 1px solid rgba(27, 31, 35, 0.06);
            
            &:hover:not(.empty-cell) {
              border-color: rgba(27, 31, 35, 0.3);
              transform: scale(1.2);
            }
            
            &.empty-cell {
              cursor: default;
              border: none;
            }
          }
        }
      }
    }
  }
}

// 响应式调整
@media (max-width: 768px) {
  .heatmap-calendar-widget {
    .heatmap-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
    
    .heatmap-container {
      overflow-x: auto;
    }
  }
}
</style>
